import { memo, useMemo, useCallback } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import CourseCard from "@/components/courses/CourseCard";
import SectionBackground from "@/components/layout/SectionBackground";
import SectionTitle from "@/components/layout/SectionTitle";
import { useCourses } from "@/hooks/useCourses";
import { useStripePayment } from "@/hooks/useStripePayment";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";

const PRICING_PAGE_COURSES = ["Follower Styling", "Partnerwork", "Leader Styling"];

const Courses = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { createPayment, loading: paymentLoading } = useStripePayment();
  const { createCheckout, loading: checkoutLoading } = useStripeCheckout();
  
  // Fetch courses with real-time updates and auto-refresh
  const { courses, loading, error } = useCourses({
    enableRealtime: !isMobile,
    autoRefresh: true,
    refreshInterval: isMobile ? 600000 : 300000,
  });
  
  const sectionRef = useRef(null);
  const cardsRef = useRef(null);

  // Track scroll progress through the section - only on desktop
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  // Memoize transform ranges for performance
  const entranceRanges = useMemo(() => ({
    opacity: isMobile ? [1, 1, 1] : [0, 0.5, 1],
    y: isMobile ? [0, 0, 0] : [80, 40, 0],
    scale: isMobile ? [1, 1, 1] : [0.95, 0.975, 1],
  }), [isMobile]);

  // Entrance parallax - disabled on mobile for performance
  const entranceOpacity = useTransform(scrollYProgress, [0, 0.25, 0.5], entranceRanges.opacity);
  const entranceY = useTransform(scrollYProgress, [0, 0.25, 0.5], entranceRanges.y);
  const entranceScale = useTransform(scrollYProgress, [0, 0.25, 0.5], entranceRanges.scale);

  // Track scroll progress for cards animation - disabled on mobile
  const { scrollYProgress: cardsProgress } = useScroll({
    target: cardsRef,
    offset: ["start 0.7", "start 0.3"],
  });

  // Memoize card animation ranges
  const cardRanges = useMemo(() => ({
    titleOpacity: isMobile ? [1, 1] : [0, 1],
    titleY: isMobile ? [0, 0] : [40, 0],
    middleScale: isMobile ? [1, 1] : [1.15, 1],
    middleZ: isMobile ? [0, 0] : [20, 0],
    leftOpacity: isMobile ? [1, 1, 1] : [0, 0.5, 1],
    leftScale: isMobile ? [1, 1] : [0.8, 1],
    rightOpacity: isMobile ? [1, 1, 1] : [0, 0.5, 1],
    rightScale: isMobile ? [1, 1] : [0.8, 1],
  }), [isMobile]);

  // Title animation - disabled on mobile
  const titleOpacity = useTransform(scrollYProgress, [0.05, 0.3], cardRanges.titleOpacity);
  const titleY = useTransform(scrollYProgress, [0.05, 0.3], cardRanges.titleY);

  // Card animations - all disabled on mobile for performance
  const middleScale = useTransform(cardsProgress, [0, 1], cardRanges.middleScale);
  const middleZ = useTransform(cardsProgress, [0, 1], cardRanges.middleZ);
  const leftOpacity = useTransform(cardsProgress, [0, 0.3, 1], cardRanges.leftOpacity);
  const leftScale = useTransform(cardsProgress, [0, 1], cardRanges.leftScale);
  const leftZ = useTransform(cardsProgress, [0, 1], [0, 0]);
  const rightOpacity = useTransform(cardsProgress, [0, 0.3, 1], cardRanges.rightOpacity);
  const rightScale = useTransform(cardsProgress, [0, 1], cardRanges.rightScale);
  const rightZ = useTransform(cardsProgress, [0, 1], [0, 0]);

  // Get CTA text for a course
  const getCtaText = useCallback((course: typeof courses[0]): string => {
    if (course.badge === "Invite Only") return course.ctaText || "Apply Now";
    if (course.stripePriceId) return course.ctaText || "Start Learning →";
    return course.ctaText || "Start Learning →";
  }, []);




  // Handle CTA click - direct checkout via stripePriceId
  const handleCtaClick = useCallback((course: typeof courses[0]) => {
    if (course.badge === "Invite Only") {
      navigate(`/apply?program=${encodeURIComponent(course.title)}`);
      return;
    }
    
    // Main subscription courses go to pricing page
    if (PRICING_PAGE_COURSES.includes(course.title)) {
      navigate('/pricing');
      return;
    }
    
    if (course.stripePriceId) {
      createPayment(course.stripePriceId);
      return;
    }
    
    // Fallback: go to pricing page
    navigate('/pricing');
  }, [navigate, createPayment]);

  const isProcessing = paymentLoading || checkoutLoading;

  return (
    <section ref={sectionRef} className="relative pt-4 md:pt-16 pb-12 md:pb-16 px-4" style={{ position: 'relative' }}>
      <SectionBackground />
      
      <motion.div 
        className="relative max-w-7xl mx-auto"
        style={courses.length > 0 ? {
          opacity: entranceOpacity,
          y: entranceY,
          scale: entranceScale,
        } : {}}
        transition={{ ease: [0.25, 0.1, 0.25, 1] }}
      >
        {/* Section Header - Scroll-triggered with stagger */}
        {courses.length > 0 && (
          <motion.div 
            style={{ 
              opacity: titleOpacity,
              y: titleY,
            }} 
            className="mb-24"
          >
            <SectionTitle
              title="Our Most Loved Courses"
              accent="Courses"
              subtitle="Programs designed by Klau y Ros to refine your artistry, structure your growth, and master performance."
              align="center"
              size="lg"
            />
          </motion.div>
        )}

        {/* Course Cards Container - Always exists for ref */}
        <div ref={cardsRef} className="relative" style={{ position: 'relative' }}>
          {/* Loading State */}
          {loading && courses.length === 0 && (
            <div className="text-center py-20">
              <div className="animate-pulse">
                <div className="h-8 bg-white/10 rounded-lg w-64 mx-auto mb-4"></div>
                <div className="h-4 bg-white/5 rounded-lg w-96 mx-auto"></div>
              </div>
            </div>
          )}
          
          {/* Error State - Only shown if no courses at all */}
          {error && courses.length === 0 && (
            <div className="text-center py-20">
              <p className="text-destructive">Failed to load courses. Please try again later.</p>
            </div>
          )}
          
          {/* Course Cards - Scroll-controlled reveal */}
          {courses.length > 0 && (
          <>
            {/* First Row - Top 3 Courses */}
            <div
              className="relative grid md:grid-cols-2 lg:grid-cols-3 gap-8 px-4 md:px-0 mb-20"
              style={{ perspective: "1000px" }}
            >
            {courses.slice(0, 3).map((course, index) => {
              // Simplified animation for first row
              let cardStyle = {};
              let zIndexValue = 10;

              if (index === 0) {
                cardStyle = { opacity: leftOpacity, scale: leftScale, z: leftZ };
                zIndexValue = 5;
              } else if (index === 1) {
                cardStyle = { scale: middleScale, z: middleZ, opacity: 1 };
                zIndexValue = 20;
              } else {
                cardStyle = { opacity: rightOpacity, scale: rightScale, z: rightZ };
                zIndexValue = 5;
              }

              // Responsive ordering: maintain for first 3
              const orderClass = index === 0 ? "order-3 md:order-1" : 
                               index === 1 ? "order-1 md:order-2" : 
                               "order-2 md:order-3";

              const ctaText = getCtaText(course);

              return (
                <motion.div
                  key={course.title}
                  style={isMobile ? {} : { 
                    ...cardStyle, 
                    zIndex: zIndexValue,
                    transformStyle: "preserve-3d",
                  }}
                  className={`${orderClass}`}
                >
                  <CourseCard
                    title={course.title}
                    tagline={course.tagline}
                    description={course.description}
                    image={course.staticImage}
                    video={course.videoUrl}
                    instructor={{
                      name: course.instructor,
                      image: course.instructorImage,
                    }}
                    gradient={course.gradient}
                    badge={course.badge}
                    ctaText={ctaText}
                    onCtaClick={() => handleCtaClick(course)}
                    isLoading={isProcessing && !!course.stripePriceId}
                  />
                </motion.div>
              );
            })}
            </div>

            {/* Section Title - Exclusive Programs */}
            {courses.length > 3 && (
              <motion.div
                initial={isMobile ? { opacity: 1 } : { opacity: 0, y: 20 }}
                whileInView={isMobile ? {} : { opacity: 1, y: 0 }}
                viewport={isMobile ? { once: true, margin: "-200px" } : { once: true }}
                transition={isMobile ? {} : { duration: 0.8 }}
                className="text-center mb-16"
              >
                <h3
                  className="text-3xl md:text-4xl font-bold mb-2"
                  style={{
                    fontFamily: "'Grift', sans-serif",
                    color: "#FFFFFF",
                    letterSpacing: "-0.02em",
                  }}
                >
                  <span style={{ color: "#E67E22" }}>Exclusive</span> Programs
                </h3>
              </motion.div>
            )}

            {/* Second Row - Bottom 3 Courses */}
            {courses.length > 3 && (
              <div
                className="relative grid md:grid-cols-2 lg:grid-cols-3 gap-8 px-4 md:px-0"
                style={{ perspective: "1000px" }}
              >
              {courses.slice(3).map((course, index) => {
                // Simple fade in for second row
                const cardStyle = { opacity: 1, scale: 1 };
                const zIndexValue = 10;

                const ctaText = getCtaText(course);

                return (
                  <motion.div
                    key={course.title}
                    initial={isMobile ? { opacity: 1 } : { opacity: 0, y: 20 }}
                    whileInView={isMobile ? {} : { opacity: 1, y: 0 }}
                    viewport={isMobile ? { once: true, margin: "-200px" } : { once: true }}
                    transition={isMobile ? {} : { duration: 0.6, delay: index * 0.1 }}
                    style={isMobile ? {} : { 
                      ...cardStyle, 
                      zIndex: zIndexValue,
                      transformStyle: "preserve-3d",
                    }}
                  >
                    <CourseCard
                      title={course.title}
                      tagline={course.tagline}
                      description={course.description}
                      image={course.staticImage}
                      video={course.videoUrl}
                      instructor={{
                        name: course.instructor,
                        image: course.instructorImage,
                      }}
                      gradient={course.gradient}
                      badge={course.badge}
                      ctaText={ctaText}
                      onCtaClick={() => handleCtaClick(course)}
                      isLoading={isProcessing && !!course.stripePriceId}
                    />
                  </motion.div>
                );
              })}
              </div>
            )}
          </>
          )}
        </div>
      </motion.div>
    </section>
  );
};

export default memo(Courses);
